import { FirebaseError } from 'firebase/app';
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  isSignInWithEmailLink,
  OAuthProvider,
  sendPasswordResetEmail,
  sendSignInLinkToEmail,
  signInWithEmailAndPassword,
  signInWithEmailLink,
  signOut,
  signInWithPopup,
  type User,
} from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';
import { auth, db } from './firebase';
import {
  defaultReturnTo,
  parseReturnTo,
  redirectWithHandoff,
  type ParsedReturn,
} from './handoff';

const RADLEY_MARK =
  'https://firebasestorage.googleapis.com/v0/b/finli-efed8.firebasestorage.app/o/tempo%2FUntitled-2-3.png?alt=media&token=0473db58-9789-4907-942f-a0bf9ec52f90';
const MAGIC_LINK_EMAIL_KEY = 'radley_auth_magic_link_email';
const MAGIC_LINK_RETURN_KEY = 'radley_auth_magic_link_return';

type AuthMode = 'login' | 'signup';

interface AuthPageProps {
  mode: AuthMode;
  onSwitchMode: (next: AuthMode) => void;
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 48 48" className="h-5 w-5">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6 1.54 7.39 2.83l5.4-5.26C33.66 3.58 29.24 1.5 24 1.5 14.82 1.5 7.09 7.78 4.44 16.08l6.76 5.24C12.34 15.24 17.62 9.5 24 9.5z"
      />
      <path
        fill="#34A853"
        d="M46.5 24.5c0-1.64-.15-3.21-.44-4.71H24v9h12.7c-.55 2.95-2.18 5.46-4.66 7.13l7.1 5.5C43.68 37.44 46.5 31.5 46.5 24.5z"
      />
      <path
        fill="#4A90E2"
        d="M10.94 21.32c-.48 1.42-.75 2.94-.75 4.5s.27 3.08.75 4.5l-6.76 5.24C2.49 32.85 1.5 28.8 1.5 24s.99-8.85 2.68-11.56l6.76 5.24z"
      />
      <path
        fill="#FBBC04"
        d="M24 46.5c6.12 0 11.26-2.01 15-5.47l-7.1-5.5c-1.9 1.28-4.33 2.05-7.9 2.05-6.38 0-11.66-5.74-12.8-13.32l-6.76 5.24C7.09 40.23 14.82 46.5 24 46.5z"
      />
    </svg>
  );
}

function MicrosoftIcon() {
  return (
    <span className="grid h-4 w-4 grid-cols-2 gap-px">
      <span className="bg-[#F25022]" />
      <span className="bg-[#7FBA00]" />
      <span className="bg-[#00A4EF]" />
      <span className="bg-[#FFB900]" />
    </span>
  );
}

function usePrefersDark(): boolean {
  const [isDark, setIsDark] = useState<boolean>(() =>
    typeof window !== 'undefined'
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
      : true,
  );
  useEffect(() => {
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const listener = (event: MediaQueryListEvent) => setIsDark(event.matches);
    mql.addEventListener('change', listener);
    return () => mql.removeEventListener('change', listener);
  }, []);
  return isDark;
}

export default function AuthPage({ mode, onSwitchMode }: AuthPageProps) {
  const isDark = usePrefersDark();

  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const brand = params.get('brand');
  const hasReturnToParam = Boolean(params.get('returnTo'));
  const returnTo: ParsedReturn = useMemo(
    () => parseReturnTo(params.get('returnTo')) ?? defaultReturnTo(brand),
    [params, brand],
  );

  const [existingUser, setExistingUser] = useState<User | null | undefined>(undefined);
  const [isAutoHandoff, setIsAutoHandoff] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailView, setEmailView] = useState<'signin' | 'reset'>('signin');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [isSendingMagicLink, setIsSendingMagicLink] = useState(false);
  const [activeProvider, setActiveProvider] = useState<'google' | 'microsoft' | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [emailOptIn, setEmailOptIn] = useState(true);

  const isLogin = mode === 'login';
  const isSignup = mode === 'signup';

  useEffect(() => {
    document.title = isLogin ? 'Sign in · Radley' : 'Create your Radley account';
  }, [isLogin]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setExistingUser(user);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!existingUser) return;
    if (!hasReturnToParam) return;
    if (isSubmitting) return;
    if (isAutoHandoff) return;
    setIsAutoHandoff(true);
    void redirectWithHandoff(returnTo);
  }, [existingUser, hasReturnToParam, isSubmitting, isAutoHandoff, returnTo]);

  useEffect(() => {
    setEmail('');
    setPassword('');
    setFirstName('');
    setLastName('');
    setShowEmailForm(false);
    setEmailView('signin');
    setErrorMessage(null);
    setInfoMessage(null);
    setActiveProvider(null);
    setEmailOptIn(true);
  }, [mode]);

  useEffect(() => {
    if (!isLogin) return;
    if (!isSignInWithEmailLink(auth, window.location.href)) return;

    let cancelled = false;

    const complete = async () => {
      setIsSubmitting(true);
      setErrorMessage(null);
      setInfoMessage('Completing sign-in...');

      let emailForSignIn = '';
      try {
        emailForSignIn = localStorage.getItem(MAGIC_LINK_EMAIL_KEY) || '';
      } catch {
        emailForSignIn = '';
      }

      if (!emailForSignIn) {
        const prompted = window.prompt('Confirm the email address that received this sign-in link.');
        emailForSignIn = (prompted || '').trim();
      }

      if (!emailForSignIn) {
        if (!cancelled) {
          setErrorMessage('Email is required to complete sign-in.');
          setInfoMessage(null);
          setIsSubmitting(false);
        }
        return;
      }

      try {
        const result = await signInWithEmailLink(auth, emailForSignIn, window.location.href);
        try {
          localStorage.removeItem(MAGIC_LINK_EMAIL_KEY);
        } catch {
          // ignore
        }
        await upsertUserProfile(result.user, emailForSignIn);
        let storedReturn: ParsedReturn | null = null;
        try {
          const raw = localStorage.getItem(MAGIC_LINK_RETURN_KEY);
          storedReturn = parseReturnTo(raw);
          localStorage.removeItem(MAGIC_LINK_RETURN_KEY);
        } catch {
          storedReturn = null;
        }
        await redirectWithHandoff(storedReturn ?? returnTo);
      } catch (error) {
        console.error('Magic link completion error:', error);
        if (!cancelled) {
          setErrorMessage('This sign-in link is invalid or has expired. Request a new one.');
          setInfoMessage(null);
          setIsSubmitting(false);
        }
      }
    };

    void complete();

    return () => {
      cancelled = true;
    };
  }, [isLogin, returnTo]);

  const upsertUserProfile = async (user: User, fallbackEmail?: string, isNewUser = false) => {
    const displayName = user.displayName || '';
    const parts = displayName.split(' ');
    const userFirstName = parts[0] || '';
    const userLastName = parts.slice(1).join(' ');

    const baseData = {
      email: user.email || fallbackEmail || '',
      displayName,
      firstName: userFirstName,
      lastName: userLastName,
      updatedAt: new Date().toISOString(),
      lastLoginAt: new Date().toISOString(),
    };

    const newUserData = isNewUser
      ? {
          createdAt: new Date().toISOString(),
          brand: brand || 'tempo',
          tempoMember: brand !== 'labs',
          tempo: brand !== 'labs',
          labsMember: brand === 'labs',
          membershipType: 'runner',
        }
      : {};

    await setDoc(doc(db, 'users', user.uid), { ...baseData, ...newUserData }, { merge: true });
  };

  const writeSignupPreferences = async (uid: string) => {
    await setDoc(
      doc(db, 'users', uid),
      {
        notificationPreferences: {
          marketingEmails: emailOptIn,
          oneToOneEmails: emailOptIn,
          supportAndOperationalMessages: true,
          lastSyncedAt: new Date().toISOString(),
        },
      },
      { merge: true },
    );
  };

  const handleSso = async (provider: 'google' | 'microsoft') => {
    setIsSubmitting(true);
    setActiveProvider(provider);
    setErrorMessage(null);
    setInfoMessage(null);

    try {
      const authProvider =
        provider === 'google' ? new GoogleAuthProvider() : new OAuthProvider('microsoft.com');
      const result = await signInWithPopup(auth, authProvider);
      await upsertUserProfile(result.user, undefined, isSignup);
      if (isSignup) await writeSignupPreferences(result.user.uid);
      await redirectWithHandoff(returnTo);
    } catch (error) {
      console.error('SSO error:', error);
      setErrorMessage('Failed to sign in. Please try again.');
      setIsSubmitting(false);
      setActiveProvider(null);
    }
  };

  const handleEmailSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    setErrorMessage(null);
    setInfoMessage(null);

    try {
      if (isLogin) {
        const result = await signInWithEmailAndPassword(auth, email, password);
        await upsertUserProfile(result.user, email);
        await redirectWithHandoff(returnTo);
      } else {
        const result = await createUserWithEmailAndPassword(auth, email, password);
        const displayName = `${firstName} ${lastName}`.trim();
        await setDoc(doc(db, 'users', result.user.uid), {
          email,
          firstName,
          lastName,
          displayName,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          brand: brand || 'tempo',
          tempoMember: brand !== 'labs',
          tempo: brand !== 'labs',
          labsMember: brand === 'labs',
          membershipType: 'runner',
          notificationPreferences: {
            marketingEmails: emailOptIn,
            oneToOneEmails: emailOptIn,
            supportAndOperationalMessages: true,
            lastSyncedAt: new Date().toISOString(),
          },
        });
        await redirectWithHandoff(returnTo);
      }
    } catch (error) {
      console.error('Email auth error:', error);
      let message = 'An error occurred. Please try again.';
      if (error instanceof FirebaseError) {
        const map: Record<string, string> = {
          'auth/invalid-credential': 'Incorrect email or password.',
          'auth/wrong-password': 'Incorrect email or password.',
          'auth/user-not-found': 'No account found with this email.',
          'auth/too-many-requests': 'Too many attempts. Please try again later.',
          'auth/email-already-in-use': 'An account with this email already exists.',
          'auth/weak-password': 'Password is too weak. Please choose a stronger password.',
          'auth/invalid-email': 'Invalid email address.',
        };
        message = map[error.code] || error.message || message;
      } else if (error instanceof Error && error.message) {
        message = error.message;
      }
      setErrorMessage(message);
      setIsSubmitting(false);
      setActiveProvider(null);
    }
  };

  const handleForgotPassword = async () => {
    if (!isLogin) return;
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setErrorMessage(null);
      setInfoMessage('Add your email address, then tap "Send reset link".');
      return;
    }
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(trimmedEmail)) {
      setInfoMessage(null);
      setErrorMessage('Enter a valid email address.');
      return;
    }

    setIsSendingReset(true);
    setErrorMessage(null);
    setInfoMessage(null);

    try {
      await sendPasswordResetEmail(auth, trimmedEmail, {
        url: `${window.location.origin}/login${window.location.search}`,
        handleCodeInApp: false,
      });
      setInfoMessage(
        `If an account exists for ${trimmedEmail}, a password reset email is on its way. Check your inbox and spam folder.`,
      );
    } catch (error) {
      console.error('Password reset error:', error);
      let message = 'Could not send password reset email.';
      if (error instanceof FirebaseError) {
        if (error.code === 'auth/user-not-found') {
          setInfoMessage(`If an account exists for ${trimmedEmail}, a password reset email is on its way.`);
          setIsSendingReset(false);
          return;
        }
        message = error.message || message;
      }
      setErrorMessage(message);
    } finally {
      setIsSendingReset(false);
    }
  };

  const handleSendMagicLink = async () => {
    if (!isLogin) return;
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setErrorMessage(null);
      setInfoMessage('Add your email address, then tap "Send magic link".');
      return;
    }

    setIsSendingMagicLink(true);
    setErrorMessage(null);
    setInfoMessage(null);

    try {
      const actionCodeSettings = {
        url: `${window.location.origin}/login${window.location.search}`,
        handleCodeInApp: true,
      };
      await sendSignInLinkToEmail(auth, trimmedEmail, actionCodeSettings);
      try {
        localStorage.setItem(MAGIC_LINK_EMAIL_KEY, trimmedEmail);
        localStorage.setItem(MAGIC_LINK_RETURN_KEY, returnTo.target.toString());
      } catch {
        // ignore
      }
      setInfoMessage('Magic sign-in link sent. Open the email on this device to sign in.');
    } catch (error) {
      console.error('Magic link send error:', error);
      let message = 'Could not send magic sign-in link.';
      if (error instanceof FirebaseError && error.message) message = error.message;
      setErrorMessage(message);
    } finally {
      setIsSendingMagicLink(false);
    }
  };

  const cardClass = isDark
    ? 'bg-slate-900/60 border-slate-800 shadow-lg'
    : 'bg-white border-slate-200/80 shadow-sm';
  const inputClass = isDark
    ? 'bg-slate-800/50 border-slate-700 text-white placeholder-slate-500 focus:border-slate-600 focus:ring-1 focus:ring-slate-600'
    : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400 focus:border-slate-300 focus:ring-1 focus:ring-slate-300 focus:bg-white';
  const labelClass = isDark ? 'text-slate-400' : 'text-slate-500';
  const ssoButtonClass = isDark
    ? 'border-slate-700 text-slate-200 hover:bg-slate-800/50'
    : 'border-slate-200 text-slate-700 hover:bg-slate-50';
  const primaryButtonClass = isDark
    ? 'bg-white text-slate-900 hover:bg-slate-100'
    : 'bg-slate-900 text-white hover:bg-slate-800';
  const dividerClass = isDark ? 'border-slate-800' : 'border-slate-100';
  const dividerTextClass = isDark ? 'bg-slate-900/60 text-slate-500' : 'bg-white text-slate-400';

  const pageBg = isDark ? '#0b0f17' : '#f8fafc';

  if (existingUser === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center" style={{ background: pageBg }}>
        <div className="text-center">
          <div
            className={`mx-auto mb-3 h-5 w-5 animate-spin rounded-full border-2 ${
              isDark ? 'border-slate-700 border-t-slate-200' : 'border-slate-300 border-t-slate-700'
            }`}
          />
          <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Checking your session…</p>
        </div>
      </div>
    );
  }

  if (existingUser && (hasReturnToParam || isAutoHandoff)) {
    return (
      <div className="flex min-h-screen items-center justify-center" style={{ background: pageBg }}>
        <div className="text-center">
          <div
            className={`mx-auto mb-3 h-5 w-5 animate-spin rounded-full border-2 ${
              isDark ? 'border-slate-700 border-t-slate-200' : 'border-slate-300 border-t-slate-700'
            }`}
          />
          <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
            Signing you in to {returnTo.target.hostname}…
          </p>
        </div>
      </div>
    );
  }

  if (existingUser) {
    const name = existingUser.displayName?.trim() || existingUser.email || 'there';
    return (
      <div className="min-h-screen flex flex-col" style={{ background: pageBg }}>
        <div className="mx-auto flex-1 w-full max-w-xl px-4 pt-16 pb-12 sm:px-8 sm:pt-24 sm:pb-24">
          <div className={`rounded-2xl border p-5 sm:p-8 ${cardClass}`}>
            <div className="mb-6 flex items-center gap-3 sm:mb-8">
              <div
                className={`rounded-2xl p-2.5 shadow-sm sm:p-3 ${
                  isDark ? 'bg-slate-800 ring-1 ring-slate-700' : 'bg-white ring-1 ring-slate-200'
                }`}
              >
                <img src={RADLEY_MARK} alt="Radley" className="h-10 w-10 rounded-xl object-contain" />
              </div>
              <div>
                <p className={`text-[11px] font-medium uppercase tracking-[0.2em] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                  Radley account
                </p>
                <p className={`text-sm font-semibold ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>
                  Signed in as {name}
                </p>
              </div>
            </div>

            <h1
              className={`mb-2 text-left text-xl font-semibold tracking-tight sm:text-2xl ${isDark ? 'text-white' : 'text-slate-900'}`}
            >
              You&apos;re already signed in.
            </h1>
            <p className={`mb-6 text-left text-[13px] leading-relaxed sm:text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              Head to a Radley app, or sign out to use a different account.
            </p>

            <div className="space-y-3">
              <a
                href="https://tempo.radley.pro"
                className={`block w-full rounded-lg px-4 py-3 text-center text-sm font-medium transition ${primaryButtonClass}`}
              >
                Continue to &gt;tempo
              </a>
              <a
                href="https://labs.radley.pro"
                className={`block w-full rounded-lg border px-4 py-3 text-center text-sm font-medium transition ${ssoButtonClass}`}
              >
                Continue to Radley Labs
              </a>
              <button
                type="button"
                onClick={() => {
                  void signOut(auth);
                }}
                className={`block w-full rounded-lg px-4 py-3 text-center text-sm font-medium transition ${
                  isDark ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ background: pageBg }}>
      <div className="mx-auto max-w-xl w-full px-4 pt-7 sm:px-8 sm:pt-14">
        <div className="mb-4 flex justify-start sm:mb-6">
          <a
            href={returnTo.target.origin}
            className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-2 text-xs font-semibold transition sm:px-4 sm:text-sm ${
              isDark
                ? 'border-slate-700 text-slate-200 hover:bg-slate-800'
                : 'border-slate-200 text-slate-800 hover:bg-slate-50'
            }`}
          >
            <span className="text-lg leading-none">←</span>
            Back
          </a>
        </div>
      </div>

      <div className="mx-auto flex-1 w-full max-w-xl px-4 pb-12 sm:px-8 sm:pb-24">
        <div className={`rounded-2xl border p-5 sm:p-8 ${cardClass}`}>
          <div className="mb-7 sm:mb-8">
            <div
              className={`-mx-5 -mt-5 mb-5 border-b px-5 py-4 sm:-mx-8 sm:-mt-8 sm:mb-6 sm:px-8 sm:py-5 ${
                isDark ? 'border-slate-800 bg-slate-950/50' : 'border-slate-200 bg-slate-100/80'
              }`}
            >
              <div className="flex items-center justify-start gap-3 sm:gap-4">
                <div
                  className={`rounded-2xl p-2.5 shadow-sm sm:p-3 ${
                    isDark ? 'bg-slate-800 ring-1 ring-slate-700' : 'bg-white ring-1 ring-slate-200'
                  }`}
                >
                  <img src={RADLEY_MARK} alt="Radley" className="h-12 w-12 rounded-xl object-contain sm:h-16 sm:w-16" />
                </div>
                <div>
                  <p className={`text-[11px] font-medium uppercase tracking-[0.2em] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                    Radley account
                  </p>
                  <p className={`text-sm font-semibold ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>
                    One sign-in for every Radley app
                  </p>
                </div>
              </div>
            </div>

            <h1
              className={`mb-2 text-left text-xl font-semibold tracking-tight sm:text-2xl ${isDark ? 'text-white' : 'text-slate-900'}`}
            >
              {isLogin ? 'Welcome back' : 'Create your account'}
            </h1>
            <p className={`text-left text-[13px] leading-relaxed sm:text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              {isLogin
                ? 'Sign in to your Radley account to continue.'
                : 'Create a Radley account to access every Radley app.'}
            </p>
          </div>

          {errorMessage && (
            <div
              className={`mb-5 rounded-lg px-3.5 py-3 text-[13px] sm:px-4 sm:text-sm ${
                isDark ? 'bg-red-500/10 text-red-300' : 'bg-red-50 text-red-600'
              }`}
            >
              {errorMessage}
            </div>
          )}

          {infoMessage && (
            <div
              className={`mb-5 rounded-lg px-3.5 py-3 text-[13px] sm:px-4 sm:text-sm ${
                isDark ? 'bg-blue-500/10 text-blue-300' : 'bg-blue-50 text-blue-700'
              }`}
            >
              {infoMessage}
            </div>
          )}

          {showEmailForm && emailView === 'reset' ? (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                void handleForgotPassword();
              }}
              className="space-y-4"
            >
              <button
                type="button"
                onClick={() => {
                  setEmailView('signin');
                  setErrorMessage(null);
                  setInfoMessage(null);
                }}
                className={`mb-1.5 flex items-center gap-1.5 text-xs font-medium sm:mb-2 ${
                  isDark ? 'text-slate-500 hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'
                }`}
              >
                ← Back to sign in
              </button>

              <div>
                <p className={`mb-4 text-[13px] leading-relaxed sm:text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  Enter the email associated with your account and we&apos;ll send a secure link to reset your password.
                </p>
                <label className={`block text-xs font-medium mb-1.5 ${labelClass}`}>Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  autoComplete="email"
                  className={`w-full rounded-lg border px-3 py-3 text-sm transition focus:outline-none ${inputClass}`}
                  placeholder="you@example.com"
                />
              </div>

              <button
                type="submit"
                disabled={isSendingReset}
                className={`mt-2 w-full rounded-lg px-4 py-3 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${primaryButtonClass}`}
              >
                {isSendingReset ? 'Sending reset link...' : 'Send reset link'}
              </button>
            </form>
          ) : showEmailForm ? (
            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <button
                type="button"
                onClick={() => {
                  setShowEmailForm(false);
                  setErrorMessage(null);
                }}
                className={`mb-1.5 flex items-center gap-1.5 text-xs font-medium sm:mb-2 ${
                  isDark ? 'text-slate-500 hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'
                }`}
              >
                ← Back
              </button>

              {isSignup && (
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <label className={`block text-xs font-medium mb-1.5 ${labelClass}`}>First name</label>
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                      className={`w-full rounded-lg border px-3 py-3 text-sm transition focus:outline-none ${inputClass}`}
                      placeholder="Alex"
                    />
                  </div>
                  <div>
                    <label className={`block text-xs font-medium mb-1.5 ${labelClass}`}>Last name</label>
                    <input
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                      className={`w-full rounded-lg border px-3 py-3 text-sm transition focus:outline-none ${inputClass}`}
                      placeholder="Runner"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className={`block text-xs font-medium mb-1.5 ${labelClass}`}>Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  className={`w-full rounded-lg border px-3 py-3 text-sm transition focus:outline-none ${inputClass}`}
                  placeholder="you@example.com"
                />
              </div>

              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <label className={`text-xs font-medium ${labelClass}`}>Password</label>
                  {isLogin && (
                    <button
                      type="button"
                      onClick={() => {
                        setEmailView('reset');
                        setPassword('');
                        setErrorMessage(null);
                        setInfoMessage(null);
                      }}
                      className={`text-xs font-semibold transition ${
                        isDark ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      Forgot password?
                    </button>
                  )}
                </div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={isSignup ? 6 : undefined}
                  autoComplete={isSignup ? 'new-password' : 'current-password'}
                  className={`w-full rounded-lg border px-3 py-3 text-sm transition focus:outline-none ${inputClass}`}
                  placeholder={isSignup ? 'Min. 6 characters' : 'Enter your password'}
                />
              </div>

              {isSignup && (
                <label
                  className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 text-[13px] ${
                    isDark ? 'border-slate-700 bg-slate-900/40 text-slate-300' : 'border-slate-200 bg-slate-50 text-slate-700'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={emailOptIn}
                    onChange={(e) => setEmailOptIn(e.target.checked)}
                    className="mt-0.5"
                  />
                  <span>
                    Send me occasional emails about events, products, and updates from Radley. You can unsubscribe any time.
                  </span>
                </label>
              )}

              {isLogin && (
                <div
                  className={`flex items-center justify-between gap-3 rounded-xl border p-3.5 ${
                    isDark ? 'border-slate-700/80 bg-slate-900/35' : 'border-slate-200 bg-slate-50/80'
                  }`}
                >
                  <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    Prefer a magic link? We&apos;ll email one to sign you in.
                  </p>
                  <button
                    type="button"
                    onClick={handleSendMagicLink}
                    disabled={isSubmitting || isSendingMagicLink}
                    className={`shrink-0 rounded-md border px-3 py-2 text-xs font-medium transition disabled:opacity-50 ${
                      isDark
                        ? 'border-slate-600 text-slate-200 hover:bg-slate-700'
                        : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    {isSendingMagicLink ? 'Sending...' : 'Send link'}
                  </button>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className={`mt-2 w-full rounded-lg px-4 py-3 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${primaryButtonClass}`}
              >
                {isSubmitting ? (isLogin ? 'Signing in...' : 'Creating account...') : isLogin ? 'Sign in' : 'Create account'}
              </button>
            </form>
          ) : (
            <div className="space-y-3.5">
              {isSignup && (
                <label
                  className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 text-[13px] ${
                    isDark ? 'border-slate-700 bg-slate-900/40 text-slate-300' : 'border-slate-200 bg-slate-50 text-slate-700'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={emailOptIn}
                    onChange={(e) => setEmailOptIn(e.target.checked)}
                    className="mt-0.5"
                  />
                  <span>
                    Send me occasional emails about events, products, and updates from Radley. You can unsubscribe any time.
                  </span>
                </label>
              )}

              <button
                type="button"
                onClick={() => handleSso('google')}
                disabled={isSubmitting}
                className={`flex w-full items-center justify-center gap-3 rounded-lg border px-4 py-3 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${ssoButtonClass}`}
              >
                {activeProvider === 'google' ? (
                  <span>Connecting...</span>
                ) : (
                  <>
                    <GoogleIcon />
                    <span>Continue with Google</span>
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={() => handleSso('microsoft')}
                disabled={isSubmitting}
                className={`flex w-full items-center justify-center gap-3 rounded-lg border px-4 py-3 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${ssoButtonClass}`}
              >
                {activeProvider === 'microsoft' ? (
                  <span>Connecting...</span>
                ) : (
                  <>
                    <MicrosoftIcon />
                    <span>Continue with Microsoft</span>
                  </>
                )}
              </button>

              <div className="relative py-4">
                <div className="absolute inset-0 flex items-center">
                  <div className={`w-full border-t ${dividerClass}`} />
                </div>
                <div className="relative flex justify-center">
                  <span className={`px-3 text-xs ${dividerTextClass}`}>or</span>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setShowEmailForm(true)}
                className={`w-full rounded-lg px-4 py-3 text-sm font-medium transition ${primaryButtonClass}`}
              >
                Continue with email
              </button>
            </div>
          )}

          <p
            className={`mt-5 border-t pt-5 text-left text-xs leading-relaxed ${
              isDark ? 'text-slate-500 border-slate-800' : 'text-slate-400 border-slate-100'
            }`}
          >
            {isLogin ? "Don't have an account?" : 'Already have an account?'}{' '}
            <button
              type="button"
              onClick={() => onSwitchMode(isLogin ? 'signup' : 'login')}
              className={`font-medium ${isDark ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}
            >
              {isLogin ? 'Create one' : 'Sign in'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
